/* eslint-disable react-refresh/only-export-components */
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { ConfigProvider as AntdConfigProvider } from 'antd';
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAutomatedBrowser } from '../domain/automatedBrowser';
import { getAntdLocale } from './antdLocale';
import { buildAntdTheme } from './antdTheme';
import { corporateBlueTheme } from './themes/corporate-blue';
import { customTheme } from './themes/custom';
import { darkTheme } from './themes/dark';
import { defaultTheme } from './themes/default';
import { elegantGoldTheme } from './themes/elegant-gold';
import { gyurciTheme } from './themes/gyurci';
import { pro1Theme } from './themes/pro1';
import { pro2Theme } from './themes/pro2';

export type ThemeName =
    | 'default'
    | 'dark'
    | 'corporate-blue'
    | 'elegant-gold'
    | 'custom'
    | 'pro1'
    | 'pro2'
    | 'gyurci';

interface ThemeContextType {
    currentTheme: ThemeName;
    setTheme: (theme: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'pawnshop-theme';

const themeMap = {
    default: defaultTheme,
    dark: darkTheme,
    'corporate-blue': corporateBlueTheme,
    'elegant-gold': elegantGoldTheme,
    custom: customTheme,
    pro1: pro1Theme,
    pro2: pro2Theme,
    gyurci: gyurciTheme,
};

/**
 * No component animations under browser automation (Playwright); people at a counter keep them.
 *
 * A spec that clicks inside a dialog still zooming in loses the click, and an exit motion whose end
 * event never arrives leaves a closed modal on screen - both made one main build fail at random
 * (#681, #682, branch #72). Turned off for antd through its `motion` token, which switches
 * rc-motion off rather than shortening it, and for MUI by giving every transition no duration.
 */
const MOTION_OFF = isAutomatedBrowser();

if (MOTION_OFF) {
    // `Modal.confirm` and the other static methods render outside the provider below, so they need
    // the setting on their own holder.
    AntdConfigProvider.config({
        holderRender: children => (
            <AntdConfigProvider theme={{ token: { motion: false } }}>{children}</AntdConfigProvider>
        ),
    });
}

const NO_MUI_TRANSITIONS = { transitions: { create: () => 'none' } };

const buildMuiTheme = (name: ThemeName) =>
    MOTION_OFF ? createTheme(themeMap[name], NO_MUI_TRANSITIONS) : createTheme(themeMap[name]);

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
    const { i18n } = useTranslation();
    const [currentTheme, setCurrentTheme] = useState<ThemeName>(() => {
        // Load theme from localStorage on initialization
        const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
        return (savedTheme as ThemeName) ?? 'default';
    });

    const setTheme = (theme: ThemeName) => {
        setCurrentTheme(theme);
        localStorage.setItem(THEME_STORAGE_KEY, theme);
    };

    // Apply theme to CSS variables for components that use them
    useEffect(() => {
        const theme = createTheme(themeMap[currentTheme]);
        const root = document.documentElement;

        // Set CSS variables
        root.style.setProperty('--primary-color', theme.palette.primary.main);
        root.style.setProperty('--secondary-color', theme.palette.secondary.main);
        root.style.setProperty('--background-color', theme.palette.background.default);
        root.style.setProperty('--paper-color', theme.palette.background.paper);
        root.style.setProperty('--text-primary', theme.palette.text.primary);
        root.style.setProperty('--text-secondary', theme.palette.text.secondary);

        // Expose the active theme name to CSS for theme-scoped styling
        root.setAttribute('data-theme', currentTheme);
    }, [currentTheme]);

    const theme = useMemo(() => buildMuiTheme(currentTheme), [currentTheme]);
    const antdThemeConfig = useMemo(() => {
        const config = buildAntdTheme(theme);

        return MOTION_OFF ? { ...config, token: { ...config.token, motion: false } } : config;
    }, [theme]);

    const contextValue = useMemo(
        () => ({
            currentTheme,
            setTheme,
        }),
        [currentTheme],
    );

    return (
        <ThemeContext.Provider value={contextValue}>
            <MuiThemeProvider theme={theme}>
                <AntdConfigProvider theme={antdThemeConfig} locale={getAntdLocale(i18n.language)}>
                    {children}
                </AntdConfigProvider>
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
